// ─── utils/dom.js ─────────────────────────────────────────────────────────────
// Low-level DOM helpers shared across all widget builders.

export const cs = (el) => window.getComputedStyle(el);

export function isVisible(el) {
    if (!el) return false;
    const s = cs(el);
    return (
        el.offsetParent !== null &&
        s.display       !== 'none' &&
        s.visibility    !== 'hidden' &&
        parseFloat(s.opacity) > 0
    );
}

/**
 * Returns an element's rect relative to the document origin (not viewport),
 * accounting for any current scroll position.
 */
export function getAbsoluteRect(el) {
    const r   = el.getBoundingClientRect();
    const doc = el.ownerDocument;
    const sx  = doc.documentElement.scrollLeft || doc.body.scrollLeft || 0;
    const sy  = doc.documentElement.scrollTop  || doc.body.scrollTop  || 0;
    return { top: r.top + sy, left: r.left + sx, width: r.width, height: r.height };
}

// ── Typography getters ────────────────────────────────────────────────────────
export const getFontFamily    = (el) => cs(el).fontFamily    || 'inherit';
export const getFontSize      = (el) => parseFloat(cs(el).fontSize || '16px');
export const getFontWeight    = (el) => cs(el).fontWeight    || '400';
export const getLineHeight    = (el) => parseFloat(cs(el).lineHeight) || 1.2;
export const getLetterSpacing = (el) => parseFloat(cs(el).letterSpacing) || 0;
export const getTextTransform = (el) => cs(el).textTransform || 'none';
export const getColor         = (el) => cs(el).color         || 'rgb(0,0,0)';
export const getOverflow      = (el) => cs(el).overflow      || 'visible';

// ── Alignment guard ───────────────────────────────────────────────────────────
const VALID_ALIGNMENTS = ['left', 'center', 'justify', 'right'];
export const safeAlign = (a) => (VALID_ALIGNMENTS.includes(a) ? a : 'center');

export function normalizeTextAlign(value) {
    if (value === 'start') return 'left';
    if (value === 'end') return 'right';
    return safeAlign(value);
}

export const getAlignment = (el) => normalizeTextAlign(cs(el).textAlign || 'left');

function hasExplicitTextAlign(el) {
    if (el.style?.textAlign) return true;

    const attr = el.getAttribute('style');
    if (attr && /text-align\s*:/i.test(attr)) return true;

    const doc = el.ownerDocument;
    if (!doc) return false;

    for (const sheet of Array.from(doc.styleSheets || [])) {
        let rules;
        try { rules = sheet.cssRules; } catch { continue; }
        if (!rules) continue;
        for (const rule of rules) {
            if (rule.type !== CSSRule.STYLE_RULE || !rule.style?.textAlign) continue;
            try {
                if (el.matches(rule.selectorText)) return true;
            } catch { /* invalid selector */ }
        }
    }

    return false;
}

/** Returns button text alignment, defaulting to center when none is set on the element. */
export function getButtonAlignment(el) {
    if (!hasExplicitTextAlign(el)) return 'center';
    return normalizeTextAlign(cs(el).textAlign);
}

/**
 * Extracts text from an element, preserving <br> as line breaks in the output HTML.
 */
export function getTextContentWithLineBreaks(el) {
    const parts = [];

    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            parts.push(node.textContent);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName.toLowerCase() === 'br') {
                parts.push('<br>');
            } else {
                node.childNodes.forEach(walk);
            }
        }
    };

    el.childNodes.forEach(walk);
    return parts.join('').trim();
}

// ── Text type classifier ──────────────────────────────────────────────────────
export function getTextTypeAndLevel(fontSize) {
    if (fontSize >= 32) return { type: 'heading', level: 1 };
    if (fontSize >= 24) return { type: 'heading', level: 2 };
    return { type: 'paragraph', level: null };
}

/**
 * Finds the best mobile counterpart for a desktop element.
 * Priority: src path match → text content match → index fallback.
 */
export function findMobileMatch(desktopEl, mobileDoc, selector, desktopDoc) {
    const cands = Array.from(mobileDoc.querySelectorAll(selector));
    if (!cands.length) return null;

    // 1. src path match (images / videos)
    const rawSrc = desktopEl.getAttribute('src') || '';
    if (rawSrc) {
        const normPath = (s) => { try { return new URL(s).pathname; } catch { return s; } };
        const byPath   = cands.find((e) => {
            const ms = e.getAttribute('src') || '';
            return ms && normPath(ms) === normPath(rawSrc);
        });
        if (byPath) return byPath;
    }

    // 2. text content match
    const dt = desktopEl.textContent.trim();
    if (dt) {
        const byText = cands.find((e) => e.textContent.trim() === dt);
        if (byText) return byText;
    }

    // 3. DOM index fallback
    if (desktopDoc) {
        const all = Array.from(desktopDoc.querySelectorAll(selector));
        const idx = all.indexOf(desktopEl);
        if (idx !== -1 && cands[idx]) return cands[idx];
    }

    return null;
}